import { extract as extractEmlMail } from 'letterparser';
import { extractLegacyDocText } from './legacyDocParser';
export var escapeHtml = function (value) {
    var htmlEscapeMap = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
    };
    return value.split('').map(function (char) { return htmlEscapeMap[char] || char; }).join('');
};
export function decodeArrayBufferText(arrayBufferData) {
    try {
        return new TextDecoder('utf-8').decode(arrayBufferData);
    }
    catch (_a) {
        return new TextDecoder('latin1').decode(arrayBufferData);
    }
}
export function isZipContainer(arrayBufferData) {
    var bytes = new Uint8Array(arrayBufferData);
    return bytes.length >= 4
        && bytes[0] === 0x50
        && bytes[1] === 0x4b
        && bytes[2] === 0x03
        && bytes[3] === 0x04;
}
export function cleanReadableLines(value) {
    return value
        .replace(/\u0000/g, '')
        .replace(/\r/g, '')
        .split('\n')
        .map(function (line) { return line.replace(/[^\x09\x20-\x7E]/g, ' ').replace(/\s{2,}/g, ' ').trim(); })
        .filter(function (line) { return line.length > 0; })
        .filter(function (line) {
        var printable = (line.match(/[A-Za-z0-9\s.,:;@#%&()\-_/]/g) || []).length;
        return printable / Math.max(line.length, 1) > 0.7;
    });
}
function formatLegacyDocText(value) {
    var sectionLabels = [
        'Program',
        'Requested Documents',
        'Status',
        'Transaction',
        'Dealer',
        'Sales Rep',
        'Email',
        'Customer',
        'Credit Decision',
        'Credit Conditions',
        'Pricing Option',
        'Term',
        'Financed Amount',
        'Equipment',
        'Notice',
        'Date',
        'Subject',
    ];
    var formatted = value
        .replace(/\u0000/g, ' ')
        .replace(/\r/g, '\n')
        .replace(/\t/g, ' ')
        .replace(/ +/g, ' ')
        .replace(/\n{2,}/g, '\n');
    formatted = formatted
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([0-9])([A-Za-z])/g, '$1 $2')
        .replace(/([A-Za-z])([0-9])/g, '$1 $2')
        .replace(/:\s*/g, ': ')
        .replace(/#\s*/g, '#')
        .replace(/,([A-Za-z])/g, ', $1')
        .replace(/\.([A-Za-z])/g, '. $1');
    sectionLabels.forEach(function (label) {
        var escapedLabel = label.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
        var pattern = new RegExp("\\s(".concat(escapedLabel, ":)"), 'gi');
        formatted = formatted.replace(pattern, '\n$1');
    });
    return formatted
        .split('\n')
        .map(function (line) { return line.trim(); })
        .filter(function (line) { return line.length > 0; })
        .join('\n');
}
function extractLegacyDocReadableTextHeuristic(arrayBufferData) {
    var decoded = decodeArrayBufferText(arrayBufferData);
    var printableChunks = decoded.match(/[A-Za-z0-9@#%&(),.:;'"\-_/\n\r\s]{6,}/g) || [];
    var merged = printableChunks.join('\n');
    var cleaned = cleanReadableLines(merged).join('\n');
    var formatted = formatLegacyDocText(cleaned);
    return formatted.slice(0, 90000);
}
export function extractLegacyDocReadableText(arrayBufferData) {
    var parsedText = extractLegacyDocText(arrayBufferData);
    if (parsedText) {
        return parsedText.slice(0, 90000);
    }
    return extractLegacyDocReadableTextHeuristic(arrayBufferData);
}
function bytesToBase64(bytes) {
    var binary = '';
    var chunkSize = 0x8000;
    for (var i = 0; i < bytes.length; i += chunkSize) {
        var chunk = bytes.subarray(i, i + chunkSize);
        for (var j = 0; j < chunk.length; j += 1) {
            binary += String.fromCharCode(chunk[j]);
        }
    }
    return btoa(binary);
}
function attachmentBodyToBase64(body) {
    return bytesToBase64(typeof body === 'string' ? new TextEncoder().encode(body) : body);
}
function attachmentByteLength(body) {
    return typeof body === 'string' ? new TextEncoder().encode(body).length : body.length;
}
function formatAttachmentSize(byteLength) {
    if (byteLength < 1024)
        return "".concat(byteLength, " B");
    if (byteLength < 1024 * 1024)
        return "".concat((byteLength / 1024).toFixed(1), " KB");
    return "".concat((byteLength / (1024 * 1024)).toFixed(1), " MB");
}
function attachmentToDataUrl(attachment) {
    var _a;
    var mime = ((_a = attachment.contentType) === null || _a === void 0 ? void 0 : _a.type) || 'application/octet-stream';
    return "data:".concat(mime, ";base64,").concat(attachmentBodyToBase64(attachment.body));
}
var GENERIC_ATTACHMENT_MIME_TYPES = new Set(['application/octet-stream', 'binary/octet-stream', '']);
var ATTACHMENT_EXTENSION_MIME_MAP = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    txt: 'text/plain',
    htm: 'text/html',
    html: 'text/html',
};
function resolveAttachmentMime(declaredMime, filename) {
    var _a, _b;
    if (!GENERIC_ATTACHMENT_MIME_TYPES.has(declaredMime.toLowerCase())) {
        return declaredMime;
    }
    var extension = (_b = (_a = filename.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '';
    return ATTACHMENT_EXTENSION_MIME_MAP[extension] || declaredMime || 'application/octet-stream';
}
function buildAttachmentSummaries(attachments) {
    return attachments
        .filter(function (attachment) { return Boolean(attachment.filename); })
        .map(function (attachment) {
        var _a;
        var filename = attachment.filename;
        return {
            filename: filename,
            mime: resolveAttachmentMime(((_a = attachment.contentType) === null || _a === void 0 ? void 0 : _a.type) || '', filename),
            base64: attachmentBodyToBase64(attachment.body),
            sizeLabel: formatAttachmentSize(attachmentByteLength(attachment.body)),
        };
    });
}
function formatEmlMailbox(mailbox) {
    if (!mailbox)
        return undefined;
    return mailbox.name ? "".concat(mailbox.name, " <").concat(mailbox.address, ">") : mailbox.address;
}
function formatEmlMailboxList(mailboxes) {
    if (!(mailboxes === null || mailboxes === void 0 ? void 0 : mailboxes.length))
        return undefined;
    return mailboxes.map(function (mailbox) { return formatEmlMailbox(mailbox); }).join(', ');
}
export function buildEmlPreview(arrayBufferData, fileName) {
    var _a;
    var rawEmlText = decodeArrayBufferText(arrayBufferData);
    try {
        var mail = extractEmlMail(rawEmlText);
        var headerRows = [
            ['From', formatEmlMailbox(mail.from)],
            ['To', formatEmlMailboxList(mail.to)],
            ['Cc', formatEmlMailboxList(mail.cc)],
            ['Date', mail.date ? mail.date.toLocaleString() : undefined],
            ['Subject', mail.subject],
        ]
            .filter(function (_a) {
            var value = _a[1];
            return Boolean(value);
        })
            .map(function (_a) {
            var label = _a[0], value = _a[1];
            return ("<tr><td style=\"padding:2px 12px 2px 0;color:#6B7280;font-weight:600;white-space:nowrap;vertical-align:top;\">".concat(escapeHtml(label), "</td><td style=\"padding:2px 0;\">").concat(escapeHtml(value), "</td></tr>"));
        })
            .join('');
        var attachments_1 = (_a = mail.attachments) !== null && _a !== void 0 ? _a : [];
        var bodyHtml = void 0;
        if (mail.html) {
            bodyHtml = attachments_1.reduce(function (html, attachment) {
                if (!attachment.contentId)
                    return html;
                var escapedCid = attachment.contentId.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                return html.replace(new RegExp("cid:".concat(escapedCid), 'gi'), attachmentToDataUrl(attachment));
            }, mail.html);
        }
        else {
            bodyHtml = "<pre style=\"white-space:pre-wrap;word-break:break-word;font-family:inherit;font-size:13px;margin:0;\">".concat(escapeHtml(mail.text || 'No readable message body found.'), "</pre>");
        }
        var attachmentSummaries = buildAttachmentSummaries(attachments_1);
        var previewHtml = "<!DOCTYPE html>\n<html>\n<head><meta charset=\"utf-8\" /><base target=\"_blank\" /></head>\n<body style=\"margin:0;padding:16px;font-family:'Nunito',Arial,sans-serif;font-size:13px;line-height:1.5;color:#1F2937;\">\n  <table style=\"border-collapse:collapse;margin-bottom:14px;padding-bottom:12px;border-bottom:1px solid #E5E7EB;width:100%;\">".concat(headerRows, "</table>\n  <div>").concat(bodyHtml, "</div>\n</body>\n</html>");
        return {
            previewType: 'html',
            previewHtml: previewHtml,
            attachments: attachmentSummaries,
        };
    }
    catch (error) {
        var cleanBodyLines = cleanReadableLines(rawEmlText).slice(0, 240);
        return {
            previewType: 'text',
            previewHtml: "\n        <div style=\"font-family: Nunito, sans-serif; font-size: 13px; line-height: 1.55; color: #1F2937;\">\n          <div style=\"font-weight: 700; margin-bottom: 8px;\">".concat(escapeHtml(fileName || 'Email Message'), "</div>\n          <pre style=\"margin:0;white-space:pre-wrap;word-break:break-word;font-family:Menlo, Monaco, Consolas, monospace;font-size:12px;line-height:1.6;\">").concat(escapeHtml(cleanBodyLines.join('\n') || 'Unable to parse this email message.'), "</pre>\n        </div>\n      "),
            attachments: [],
        };
    }
}
